import * as React from 'react'
import { cn } from '@/lib/utils'

type Size = 'sm' | 'icon'

const sizeClasses: Record<Size, string> = {
  sm: 'h-9 w-9 rounded-full',
  icon: 'h-10 w-10 rounded-full',
}

/**
 * Icon-only button — requires an accessible name.
 * Enforces `aria-label` or `aria-labelledby` at the type level so a caller
 * cannot render an unlabeled icon button without a TypeScript error.
 */
type AccessibleName =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string }

export type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'aria-labelledby'> &
  AccessibleName & {
    size?: Size
  }

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, size = 'icon', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex min-h-9 min-w-9 items-center justify-center font-medium motion-transition-fast motion-safe:active:scale-[0.94] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo focus-visible:ring-offset-1 focus-visible:ring-offset-enterprise-bg disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none text-enterprise-muted hover:bg-enterprise-surface hover:text-enterprise-text border border-transparent',
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)
IconButton.displayName = 'IconButton'
